import React, { useEffect, useState, useRef, useMemo } from 'react';
import cn from 'classnames';
import MobileDetect from 'mobile-detect';

import Nav from './components/Nav';
import MainView from './components/MainView';
import About from './components/About';
import Resume from './components/Resume';
import Works from './components/Works';
import Contact from './components/Contact';
import Certificates from './components/Certificates';
import { ReactComponent as AboutIcon } from './components/Nav/person.svg';
import { ReactComponent as ResumeIcon } from './components/Nav/resume.svg';
import { ReactComponent as WorksIcon } from './components/Nav/works.svg';
import { ReactComponent as ContactIcon } from './components/Nav/contact.svg';
import { ReactComponent as CertificateIcon } from './components/Nav/certificateIcon.svg';

import styles from './App.module.css';
import Bubbles from './components/Bubbles';
import { useActiveToScrollTo } from './hooks/hooks';
import LangToggler from './components/UI/LangToggler';
import LanguageContext from './components/context/LanguageContext';
import 'react-multi-carousel/lib/styles.css';

const md = new MobileDetect(window.navigator.userAgent);

const isMobile = md.mobile() !== null;

function App() {
    const [tabActive, setTabActive] = useState('about');
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);
    const [highlightedTab, setHighlightedTab] = useState('');

    const [language, setLanguage] = useState('en');
    const provider = { language, setLanguage };

    const rootEl = useRef(null);

    const toAboutRef = useRef(null);
    const toResumeRef = useRef(null);
    const toWorksRef = useRef(null);
    const toCertificatesRef = useRef(null);
    const toContactRef = useRef(null);

    const all = 'all';

    const handleToggle = () => {
        if (language === 'en') {
            setLanguage('ua');
        } else {
            setLanguage('en');
        }
    };

    const tabs = useMemo(
        () => [
            {
                value: 'about',
                valueUA: 'Про',
                icon: <AboutIcon />,
                ref: toAboutRef,
            },
            {
                value: 'resume',
                valueUA: 'Резюме',
                icon: <ResumeIcon />,
                ref: toResumeRef,
            },
            {
                value: 'works',
                valueUA: 'Роботи',
                icon: <WorksIcon />,
                ref: toWorksRef,
            },
            {
                value: 'contact',
                valueUA: 'Контакти',
                icon: <ContactIcon />,
                ref: toContactRef,
            },
            // {
            //     value: ' certificates ',
            //     valueUA: ' Сертифікація ',
            //     icon: <CertificateIcon />,
            //     ref: toCertificatesRef,
            // },
        ],
        []
    );

    const smallDesktop = 1200;
    const isDesktop = windowWidth > smallDesktop;

    const scrollTo = (el) => {
        el.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const toggleTab = (ref, state) => {
        if (!isDesktop) {
            scrollTo(ref);
            setTimeout(() => setHighlightedTab(state), 900); // забрати, коли about стане довшим
        } else {
            setHighlightedTab('');
            setTabActive(null);
            setTimeout(() => {
                setTabActive(state);
            }, 100);
        }
    };

    useActiveToScrollTo(tabs[0].ref, tabs[0].value, setHighlightedTab);
    useActiveToScrollTo(tabs[1].ref, tabs[1].value, setHighlightedTab);
    useActiveToScrollTo(tabs[2].ref, tabs[2].value, setHighlightedTab);
    useActiveToScrollTo(tabs[3].ref, tabs[3].value, setHighlightedTab);
    // useActiveToScrollTo(tabs[4].ref, tabs[4].value, setHighlightedTab);

    useEffect(() => {
        window.onresize = () => {
            setWindowWidth(window.innerWidth);
        };
        return () => {
            window.onresize = false;
        };
    }, [windowWidth]);

    useEffect(() => {
        if (!isDesktop) {
            setTabActive(all);
        } else if (tabActive !== all) {
            setTabActive((state) => state);
        } else {
            setTabActive(tabs[0].value);
        }
    }, [isDesktop, tabActive, tabs]);

    return (
        <LanguageContext.Provider value={provider}>
            <div
                className={cn(styles.AppWrapper, {
                    [styles.ua]: language === 'ua',
                })}
                ref={rootEl}
            >
                <div className={styles.App}>
                    <Bubbles />
                    {isDesktop && <LangToggler handleToggle={handleToggle} />}

                    <div className={styles.container}>
                        <Nav
                            toggleTab={toggleTab}
                            active={tabActive}
                            tabs={tabs}
                            highlightedTab={highlightedTab}
                            handleToggle={handleToggle}
                            isDesktop={isDesktop}
                        />
                        <MainView isMobile={isMobile} />

                        <About
                            className={cn(styles.about, {
                                [styles.active]:
                                    tabActive === tabs[0].value ||
                                    tabActive === all,
                            })}
                            active={tabActive}
                            toAboutRef={toAboutRef}
                        />
                        <Resume
                            className={cn(styles.resume, {
                                [styles.active]:
                                    tabActive === tabs[1].value ||
                                    tabActive === all,
                            })}
                            active={tabActive}
                            toResumeRef={toResumeRef}
                        />
                        <Works
                            className={cn(styles.works, {
                                [styles.active]:
                                    tabActive === tabs[2].value ||
                                    tabActive === all,
                            })}
                            active={tabActive}
                            toWorksRef={toWorksRef}
                            isMobile={isMobile}
                        />
                        {/* <Certificates
                            className={cn(styles.certificates, {
                                [styles.active]:
                                    tabActive === tabs[3].value ||
                                    tabActive === all,
                            })}
                            active={tabActive}
                            toCertificatesRef={toCertificatesRef}
                            isMobile={isMobile}
                        /> */}
                        <Contact
                            className={cn(styles.contact, {
                                [styles.active]:
                                    tabActive === tabs[3].value ||
                                    tabActive === all,
                            })}
                            active={tabActive}
                            toContactRef={toContactRef}
                        />
                    </div>
                </div>
            </div>
        </LanguageContext.Provider>
    );
}

export default App;
